import {OverlayModule} from '@angular/cdk/overlay';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {NgModule} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatChipsModule} from '@angular/material/chips';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {MatStepperModule} from '@angular/material/stepper';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {IntlModule} from '@progress/kendo-angular-intl';
import {ContextMenuModule, MenuModule} from '@progress/kendo-angular-menu';
import {
  SharedModule as KendoTreeListSharedModule,
  TreeListModule
} from '@progress/kendo-angular-treelist';
import {UserSelectComponent} from '@q9elements/ui-kit/changes/shared';
import {
  NodeDaoService,
  Q9HtmlCleanupPipe,
  Q9HtmlDecoderPipe,
  Q9TransformPipe
} from '@q9elements/ui-kit/common';
import {
  BlurWrapperComponent,
  ContentLoaderComponent,
  DialogCloseButtonComponent,
  GridModule,
  IconButtonComponent,
  InfoMessageComponent,
  LinkButtonComponent,
  NodeSearchComponent,
  NotificationsModule,
  PageLoaderComponent,
  PrimaryButtonComponent,
  ProposedNodeFormComponent,
  Q9ContextMenuComponent,
  ReportsModule,
  SecondaryButtonComponent,
  SectionComponent,
  SelectModule,
  SfTypesSelectComponent,
  SupportButtonComponent,
  TagComponent,
  TagsComboboxModule,
  TagsSelectComponent,
  TextFieldComponent,
  TextareaFieldComponent,
  ThemeSwitcherComponent,
  ToolbarModule,
  UserInfoComponent
} from '@q9elements/ui-kit/components';
import {OrgNodeSidebarModule} from '@q9elements/ui-kit/refmodels';
import {CustomGridModule} from '../custom-grid/custom-grid.module';
import {InternalSidebarModule} from '../internal-sidebar/internal-sidebar.module';
import {GridContextMenuModule} from '../new-context-menu/context-menu.module';
import {SharedModule} from '../shared/shared.module';
import {SidebarWrapperModule} from '../sidebar/sidebar-wrapper.module';
import {AiChatDialogModule} from './components/ai-chat-dialog/ai-chat-dialog.module';
import {GptConversationHistoryComponent} from './components/conversation-dialog/components/gpt-conversation-history/gpt-conversation-history.component';
import {GptConversationSettingsComponent} from './components/conversation-dialog/components/gpt-conversation-settings/gpt-conversation-settings.component';
import {GptConversationComponent} from './components/conversation-dialog/components/gpt-conversation/gpt-conversation.component';
import {GptEmptyComponent} from './components/conversation-dialog/components/gpt-empty/gpt-empty.component';
import {ElementsConversationComponent} from './components/conversation-dialog/elements-conversation.component';
import {CreateNodeDialogComponent} from './components/create-node-dialog/create-node-dialog.component';
import {CreateProposedNodeDialogComponent} from './components/create-proposed-node-dialog/create-proposed-node-dialog.component';
import {CustomViewModalViewsModule} from './components/custom-metadata-view/custom-view-model-views.module';
import {CreateNewViewDialogModule} from './components/custom-metadata-view/dialogs/components/create-new-view-dialog/create-new-view-dialog.module';
import {EditCustomViewColumnsDialogComponent} from './components/custom-metadata-view/dialogs/components/edit-custom-view-columns-dialog/edit-custom-view-columns-dialog.component';
import {EditCustomViewDialogComponent} from './components/custom-metadata-view/dialogs/components/edit-custom-view-dialog/edit-custom-view-dialog.component';
import {EditFiltersDialogComponent} from './components/custom-metadata-view/dialogs/components/edit-filters-dialog/edit-filters-dialog.component';
import {EditSortDialogComponent} from './components/custom-metadata-view/dialogs/components/edit-sort-dialog/edit-sort-dialog.component';
import {DeleteNodeDialogComponent} from './components/extended/delete-node-dialog/delete-node-dialog.component';
import {GdprStatusDialogComponent} from './components/gdpr-status-dialog/gdpr-status-dialog.component';
import {MyMetadataViewComponent} from './components/my-metadata-view/my-metadata-view.component';
import {NodePathModule} from './components/node-path/node-path.module';
import {NodeTreeGridComponent} from './components/node-tree-grid/node-tree-grid.component';
import {CustomNodeAttributeModule} from './components/update-tags-dialog/components/custom-node-attribute/custom-node-attribute.module';
import {UpdateTagsDialogComponent} from './components/update-tags-dialog/update-tags-dialog.component';
import {ViewSelectorComponent} from './components/view-selector/view-selector.component';
import {TooltipOnOverflowDirective} from './directives/tooltip-on-overflow.directive';
import {EditorInterfaceComponent} from './editor-interface.component';
import {TextToHtmlPipe} from './pipes/text-to-html.pipe';
import {ColumnsGenerateService} from './services/columns-generate.service';
import {CustomViewColumnStylesService} from './services/custom-view-column-styles.service';
import {CustomViewService} from './services/custom-view.service';
import {GdprStatusesService} from './services/gpdr-statuses.service';
import {GridNodeFormatterService} from './services/grid-node-formatter.service';
import {GridUpdateService} from './services/grid-update.service';
import {NodeRemoveEmitterService} from './services/node-remove-emitter.service';
import {RequirementAttachmentsService} from './services/requirement-attachments.service';
import {TreeGridSelectorService} from './services/tree-grid-selector.service';
import {TreePaginationService} from './services/tree-pagination.service';
import {TreeMatPaginatorIntl} from './tree-mat-paginator-intl';

@NgModule({
  imports: [
    RouterModule,
    NotificationsModule,
    ReportsModule,
    SharedModule,
    DeleteNodeDialogComponent,
    SidebarWrapperModule,
    ScrollingModule,
    MatCardModule,
    MatStepperModule,
    CustomGridModule,
    TreeListModule,
    KendoTreeListSharedModule,
    IntlModule,
    MenuModule,
    ContextMenuModule,
    FormsModule,
    CreateNewViewDialogModule,
    CustomViewModalViewsModule,
    GridContextMenuModule,
    Q9TransformPipe,
    TranslateModule,
    DeleteNodeDialogComponent,
    AiChatDialogModule,
    TextToHtmlPipe,
    CustomNodeAttributeModule,
    TextToHtmlPipe,
    ThemeSwitcherComponent,
    SupportButtonComponent,
    ToolbarModule,
    SectionComponent,
    IconButtonComponent,
    GridModule,
    IconButtonComponent,
    OrgNodeSidebarModule,
    PrimaryButtonComponent,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    NodeSearchComponent,
    OverlayModule,
    PageLoaderComponent,
    ContentLoaderComponent,
    SelectModule,
    TagsSelectComponent,
    TagsComboboxModule,
    UserSelectComponent,
    TextFieldComponent,
    TextareaFieldComponent,
    InfoMessageComponent,
    InternalSidebarModule,
    Q9HtmlCleanupPipe,
    Q9HtmlDecoderPipe,
    UserInfoComponent,
    TagComponent,
    LinkButtonComponent,
    SfTypesSelectComponent,
    NodePathModule,
    ProposedNodeFormComponent,
    Q9ContextMenuComponent,
    MatChipsModule,
    BlurWrapperComponent
  ],
  declarations: [
    CreateNodeDialogComponent,
    GdprStatusDialogComponent,
    CreateProposedNodeDialogComponent,
    EditorInterfaceComponent,
    GptConversationHistoryComponent,
    GptConversationSettingsComponent,
    GptConversationComponent,
    ElementsConversationComponent,
    GptEmptyComponent,
    TooltipOnOverflowDirective,
    NodeTreeGridComponent,
    EditCustomViewDialogComponent,
    EditCustomViewColumnsDialogComponent,
    EditFiltersDialogComponent,
    MyMetadataViewComponent,
    UpdateTagsDialogComponent,
    EditSortDialogComponent,
    ViewSelectorComponent
  ],
  providers: [
    TreePaginationService,
    {provide: MatPaginatorIntl, useClass: TreeMatPaginatorIntl},
    GridNodeFormatterService,
    GdprStatusesService,
    CustomViewColumnStylesService,
    ColumnsGenerateService,
    TreeGridSelectorService,
    NodeRemoveEmitterService,
    GridUpdateService,
    CustomViewService,
    RequirementAttachmentsService,
    NodeDaoService
  ],
  exports: [EditorInterfaceComponent]
})
export class EditorInterfaceModule {}
